<template>
    <div class="workstation-screen-canvas stage">
        <pre
            class="debug"
            v-if="isDebug"
            >{{ { width, height, scale } }}</pre
        >

        <ui5-card>
            <v-stage
                ref="stage"
                :config="{
                    width: width * scale,
                    height: height * scale,
                    scaleX: scale,
                    scaleY: scale,
                }"
                @dragstart="onDragstart"
                @dragend="onDragend"
            >
                <v-layer
                    ref="backgroundLayer"
                    v-if="backgroundImage"
                >
                    <v-image :config="backgroundConfig" />
                </v-layer>
                <v-layer
                    ref="layer"
                    :key="'itemsLayerKey.' + itemsLayerKey"
                >
                    <v-group
                        v-for="item in screenItems"
                        :key="item.id"
                        @mousemove="onItemMouseOver"
                        @mouseout="onItemMouseOut"
                        :config="{
                            id: item.id.toString(),
                            x: item.left,
                            y: item.top,
                            draggable: isDraggable,
                            name: 'Group',
                        }"
                    >
                        <template
                            v-if="
                                item.type == TYPE_SPECIAL &&
                                item.key == 'product.image'
                            "
                        >
                            <v-image
                                :ref="'itemProductImage.' + item.id"
                                :config="getProductImageConfig(item)"
                                @transformend="handleTransformEnd($event, item)"
                            />
                            <v-transformer
                                v-if="handleDragstart && handleDragend"
                                :ref="'transformer.' + item.id"
                                :config="{ rotateEnabled: false }"
                            />
                        </template>
                        <template v-else-if="isValidItem(item)">
                            <v-text
                                :config="{
                                    text: getScreenItemLabel(item) + ':',
                                    fontSize: item.fontSize,
                                    fontStyle: item.fontStyle,
                                    fontFamily: UNICODE_FONTS,
                                    fill: item.color,
                                    x: 0,
                                    y: 0,
                                }"
                            />
                            <v-text
                                :config="{
                                    text:
                                        item.value == undefined
                                            ? 'waarde'
                                            : item.value,
                                    fontSize: item.fontSize * 2.4,
                                    fontStyle: item.fontStyle,
                                    fontFamily: UNICODE_FONTS,
                                    fill: item.color,
                                    x: 0,
                                    y: item.fontSize * 0.9,
                                }"
                            />
                            <v-text
                                v-if="item.valueDescription"
                                :config="{
                                    text: item.valueDescription,
                                    fontSize: item.fontSize * 1.0,
                                    fontStyle: item.fontStyle,
                                    fontFamily: UNICODE_FONTS,
                                    fill: item.color,
                                    x: 0,
                                    y: item.fontSize * 3.6,
                                }"
                            />
                            <v-path
                                v-if="item.showCheckbox"
                                :config="{
                                    x: 1 - item.fontSize * 4.0,
                                    y: 1 - item.fontSize * 0.7,
                                    scaleX: item.fontSize * 0.17,
                                    scaleY: item.fontSize * 0.17,
                                    stroke: 'black',
                                    strokeWidth: 1,
                                    data: item.selected
                                        ? checkedPath
                                        : uncheckedPath,
                                }"
                            />
                            <v-rect
                                @mousemove="onCheckboxMouseOver"
                                @mouseout="onCheckboxMouseOut"
                                v-if="item.showCheckbox"
                                @click="onCheckBoxClick(item)"
                                @touchend="onCheckBoxClick(item)"
                                :config="{
                                    x: 1 - item.fontSize * 3.5,
                                    y: 1 - item.fontSize * 0.1,
                                    width: item.fontSize * 3.0,
                                    height: item.fontSize * 3.0,
                                    fill: item.selected
                                        ? 'transparent'
                                        : 'transparent',
                                    strokeWidth: 1,
                                    stroke: 'transparent',
                                }"
                            />
                        </template>
                    </v-group>
                </v-layer>
            </v-stage>
        </ui5-card>
        <slot />
    </div>
</template>
<script>
import {
    SAWLINE_CHAR,
    TYPE_DATAFIELD,
    TYPE_SAWLINE,
    TYPE_SPECIAL,
    UNICODE_FONTS,
    specialOptions,
} from '../../shared/workstationScreens';
import { getDatafieldLabel } from '../../shared/datafield';
import html2canvas from 'html2canvas';
import IsDebugMixin from '../../mixins/is-debug';

//https://www.svgrepo.com/collection/fluent-ui-icons-outlined/
const checkedPath =
    'M18.25,3 C19.7687831,3 21,4.23121694 21,5.75 L21,18.25 C21,19.7687831 19.7687831,21 18.25,21 L5.75,21 C4.23121694,21 3,19.7687831 3,18.25 L3,5.75 C3,4.23121694 4.23121694,3 5.75,3 L18.25,3 Z M18.25,4.5 L5.75,4.5 C5.05964406,4.5 4.5,5.05964406 4.5,5.75 L4.5,18.25 C4.5,18.9403559 5.05964406,19.5 5.75,19.5 L18.25,19.5 C18.9403559,19.5 19.5,18.9403559 19.5,18.25 L19.5,5.75 C19.5,5.05964406 18.9403559,4.5 18.25,4.5 Z M10,14.4393398 L16.4696699,7.96966991 C16.7625631,7.6767767 17.2374369,7.6767767 17.5303301,7.96966991 C17.7965966,8.23593648 17.8208027,8.65260016 17.6029482,8.94621165 L17.5303301,9.03033009 L10.5303301,16.0303301 C10.2640635,16.2965966 9.84739984,16.3208027 9.55378835,16.1029482 L9.46966991,16.0303301 L6.46966991,13.0303301 C6.1767767,12.7374369 6.1767767,12.2625631 6.46966991,11.9696699 C6.73593648,11.7034034 7.15260016,11.6791973 7.44621165,11.8970518 L7.53033009,11.9696699 L10,14.4393398 L16.4696699,7.96966991 L10,14.4393398 Z';
const uncheckedPath =
    'M5.75,3 L18.25,3 C19.7687831,3 21,4.23121694 21,5.75 L21,18.25 C21,19.7687831 19.7687831,21 18.25,21 L5.75,21 C4.23121694,21 3,19.7687831 3,18.25 L3,5.75 C3,4.23121694 4.23121694,3 5.75,3 Z M5.75,4.5 C5.05964406,4.5 4.5,5.05964406 4.5,5.75 L4.5,18.25 C4.5,18.9403559 5.05964406,19.5 5.75,19.5 L18.25,19.5 C18.9403559,19.5 19.5,18.9403559 19.5,18.25 L19.5,5.75 C19.5,5.05964406 18.9403559,4.5 18.25,4.5 L5.75,4.5 Z';

const descriptionDict = {}; //Keep a cache of images by HTML to prevent flashing on rerender

export default {
    mixins: [IsDebugMixin],
    props: {
        handleDragstart: {
            type: Function,
            required: false,
        },
        handleDragend: {
            type: Function,
            required: false,
        },
        width: {
            type: Number,
            default: 1280,
        },
        height: {
            type: Number,
            default: 720,
        },
        scale: {
            type: Number,
            default: 1,
        },
        screenItems: {
            type: Array,
            required: true,
        },
        sawlines: {
            type: Array,
            required: true,
        },
        datafields: {
            type: Array,
            required: true,
        },
        checkboxClick: {
            type: Function,
            required: false,
        },
        backgroundImage: {
            type: window.Image,
            default: null,
        },
        productImage: {
            type: window.Image,
            default: null,
        },
        showSawlinesSymbol: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        checkedPath: () => checkedPath,
        uncheckedPath: () => uncheckedPath,
        UNICODE_FONTS: () => UNICODE_FONTS,
        SAWLINE_CHAR: () => SAWLINE_CHAR,
        TYPE_SPECIAL: () => TYPE_SPECIAL,
        isDraggable() {
            return this.handleDragstart && this.handleDragend;
        },
        backgroundConfig() {
            return {
                x: 0,
                y: 0,
                width: this.width,
                height: this.height,
                image: this.backgroundImage,
            };
        },
    },
    data() {
        return {
            dragItemId: null,
            itemsLayerKey: 0,
        };
    },
    updated() {
        if (this.screenItems) {
            //Attach nodes transformer (documentation says it can't be done in a proper Vue way at the moment)
            const items = this.screenItems.filter(
                (x) => x.type == TYPE_SPECIAL && x.key == 'product.image'
            );
            items.forEach((item) => {
                const image = this.$refs['itemProductImage.' + item.id];
                const transformer = this.$refs['transformer.' + item.id];
                if (image && transformer) {
                    const transformerNode = transformer[0].getNode();
                    const imageNode = image[0].getNode();
                    if (transformerNode) {
                        transformerNode.nodes(imageNode ? [imageNode] : []);
                    }
                }
            });
        }
    },
    methods: {
        handleTransformEnd($event, item) {
            const imageNode = $event.target;
            item.fontSize = imageNode.width() * imageNode.scaleX();
            item.fontStyle = imageNode.height() * imageNode.scaleY();
            const absolutePosition = imageNode.getAbsolutePosition(
                this.$refs.stage
            );
            item.top = absolutePosition.y;
            item.left = absolutePosition.x;
            imageNode.x(0);
            imageNode.y(0);
            imageNode.width(item.fontSize);
            imageNode.height(item.fontStyle);
            imageNode.scaleX(1);
            imageNode.scaleY(1);
        },
        getProductImageConfig(item) {
            return {
                width: item.fontSize / 1,
                height: item.fontStyle / 1,
                image: this.productImage,
                scaleX: 1,
                scaleY: 1,
                offsetX: 0,
                offsetY: 0,
                x: 0,
                y: 0,
            };
        },
        onDragstart(e) {
            if (this.isDraggable) {
                this.getStage().getNode().container().style.cursor = 'grabbing';
                this.handleDragstart(e);
            }
        },
        onDragend(e) {
            if (this.isDraggable) {
                const item = this.screenItems.find(
                    (i) => i.id.toString() === this.dragItemId
                );
                item.top = e.target._lastPos.y;
                item.left = e.target._lastPos.x;
                this.getStage().getNode().container().style.cursor = 'grab';
                this.handleDragend(e);
            }
        },
        getStage() {
            return this.$refs.stage;
        },
        onItemMouseOver() {
            if (this.isDraggable) {
                this.getStage().getNode().container().style.cursor = 'grab';
            }
        },
        onItemMouseOut() {
            if (this.isDraggable) {
                this.getStage().getNode().container().style.cursor = 'default';
            }
        },
        onCheckboxMouseOver() {
            this.getStage().getNode().container().style.cursor = 'pointer';
        },
        onCheckboxMouseOut() {
            this.getStage().getNode().container().style.cursor = 'default';
        },
        onCheckBoxClick(item) {
            if (item.showCheckbox && this.checkboxClick) {
                this.checkboxClick(item, !item.selected);
                this.itemsLayerKey++; //Force update
            }
        },
        isValidItem(item) {
            if (item.type === TYPE_DATAFIELD) {
                const datafield =
                    item.datafieldDetails || this.getDatafieldById(item);
                return datafield ? true : false;
            }
            if (item.type === TYPE_SAWLINE) {
                const sawline =
                    item.sawlineDetails || this.getSawlineById(item);
                return sawline ? true : false;
            }
            if (item.type === TYPE_SPECIAL) {
                return specialOptions.some((x) => x.key == item.key);
            }
            return false;
        },
        getScreenItemLabel(item) {
            if (item.type === TYPE_DATAFIELD) {
                const datafield =
                    item.datafieldDetails || this.getDatafieldById(item);
                return getDatafieldLabel(datafield);
            }
            if (item.type === TYPE_SAWLINE) {
                const sawline =
                    item.sawlineDetails || this.getSawlineById(item);
                return (
                    (this.showSawlinesSymbol ? SAWLINE_CHAR + ' ' : '') +
                    (sawline ? sawline.externalName : null)
                );
            }
            if (item.type === TYPE_SPECIAL) {
                const option = specialOptions.find((x) => x.key == item.key);
                return option ? option.label : '';
            }
            return '';
        },
        getDatafieldById(screenItem) {
            return this.datafields.find(
                (df) =>
                    parseInt(df.datafieldId) ===
                    parseInt(screenItem.datafieldId)
            );
        },
        getSawlineById(screenItem) {
            return this.sawlines.find(
                (sl) => parseInt(sl.ID) === parseInt(screenItem.sawlineId)
            );
        },
    },
};
</script>
